import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Form, Modal, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import Loader from '../../components/Loader';
import { store } from '../../redux/store';
import { persistDataStore } from '../../redux/persistStore';
import smartHttp from '../../helpers/smartHttp';
import CommonDropdown from '../../sharedcomponents/CommonDropdown/CommonDropdown';
import TextFieldInput from '../../sharedcomponents/TextFieldInput/TextFieldInput';
import { trimFormValues } from '../../helpers';
import {
  Endpoints,
  loaderStyle,
  ReduxAction,
  UserId,
} from './../../constants';
import './locations.scss';

const LocationAddEdit = ({
  show,
  handleClose,
  locationDetails,
  onSaveClick,
  loading,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [isValueChanged, setIsValueChanged] = useState(false);
  const [initialLocatonValue, setInitialLocatonValue] = useState();
  const [accountsData, setAccountsData] = useState([]);
  const [countryStateInfo, setCountryStateInfo] = useState({});
  // Super User: multi-select contracts state
  const [contractsMultiSelect, setContractsMultiSelect] = useState([]);
  const [contractsMultiSelectError, setContractsMultiSelectError] =
    useState('');
  const [initialContractNumbers, setInitialContractNumbers] = useState([]);
  let locationInitialValue = {
    officeLocationId: '-1',
    accountId: '',
    hC_AccountID: '',
    officeLocationName: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    isDeleted: 0,
    userId: UserId,
    contractNumber: '',
  };
  const dispatch = useDispatch();

  const SubmitHandler = (value) => {
    value = trimFormValues(value);

    const matchingAccount = accountsData?.find(
      (account) => account.accountId === value.accountId,
    );
    value.hC_AccountID = matchingAccount?.hC_AccountID ?? '';

    const selected = (contractsMultiSelect ?? []).filter((c) => c.checked);
    if (selected.length === 0) return;

    if (isEdit) {
      value.contractNumber = selected.map((c) => c.contractNumber).join(',');
      if (
        value.city !== locationDetails.city ||
        value.postalCode !== locationDetails.postalCode ||
        value.country !== locationDetails.country ||
        value.state !== (locationDetails.state || '')
      )
        return;
      onSaveClick(value, isEdit);
    } else {
      onSaveClick(
        { ...value, contractNumber: selected.map((c) => c.contractNumber).join(',') },
        false,
      );
    }
  };

  const validate = Yup.object({
    contractNumber: Yup.string(),
    accountId: Yup.string().required('Please select an account to proceed'),
    address_1: Yup.string()
      .trim()
      .max(255, 'Address 1 must be 255 characters or less')
      .required('Please enter address 1 to proceed'),
    address_2: Yup.string()
      .trim()
      .max(255, 'Address 2 must be 255 characters or less'),
    city: Yup.string()
      .trim()
      .max(100, 'City must be 100 characters or less')
      .required('Please select a city to proceed'),
    state: Yup.string().when([], {
      is: () =>
        countryStateInfo.filteredState &&
        countryStateInfo.filteredState.length > 0,
      then: Yup.string().trim().required('Please select a state to proceed'),
      otherwise: Yup.string(),
    }),
    postalCode: Yup.string()
      .trim()
      .max(20, 'Post code must be 20 characters or less')
      .required('Please select a post code to proceed'),
    country: Yup.string().trim().required('Please select a country to proceed'),
  });

  useEffect(() => {
    (async () => {
      try {
        // Use redux store to get country state list, if not available then fetch from API and store in redux
        let storeData = store.getState();
        let countryStateList = storeData?.locationTab?.countryStateList;
        if (!countryStateList) {
          countryStateList = await getCountryStateList();
        }

        let filteredCountry = countryStateList
          .map((el) => el.country)
          .sort(Intl.Collator().compare);

        let filteredState = [];

        setCountryStateInfo({
          CS_main: countryStateList,
          filteredCountry,
          filteredState,
        });
      } catch (error) {}

      getAccountsAll();
    })();
  }, [useSelector((x) => x.userInfo)]);

  const getCountryStateList = async () => {
    let tempCountryStateInfo = {};
    const configurationURI = Endpoints.configurationApi;
    let queryParams = {
      keyItem: `COUNTRY_STATE`,
    };
    await smartHttp
      .get(configurationURI, queryParams)
      .then((res) => {
        const response = res?.data?.results;
        response.map((item) => {
          if (item.keyItem === 'COUNTRY_STATE') {
            let parseList = JSON.parse(item.keyValue);

            tempCountryStateInfo.countryStateList = parseList;
          }
        });
        dispatch({
          type: ReduxAction.countryStateList,
          payload: tempCountryStateInfo,
        });
      })
      .catch((err) => {});

    return tempCountryStateInfo.countryStateList;
  };

  const getLocationValues = () => {
    return {
      officeLocationId: locationDetails.officeLocationId,
      accountId: locationDetails.accountId,
      contractNumber: locationDetails.contractNumber,
      hC_AccountID: locationDetails.hC_AccountID ?? '',
      officeLocationName: locationDetails.officeLocationName ?? '',
      address_1: locationDetails.address_1 ?? '',
      address_2: locationDetails.address_2 ?? '',
      city: locationDetails.city ?? '',
      state: locationDetails.state ?? '',
      postalCode: locationDetails.postalCode ?? '',
      country: locationDetails.country ?? '',
      isDeleted: locationDetails.isDeleted ?? 0,
      userId: locationDetails.userId ?? UserId,
    };
  };
  const locationFormik = useFormik({
    initialValues: isEdit ? getLocationValues() : locationInitialValue,
    validationSchema: validate,
    onSubmit: SubmitHandler,
    enableReinitialize: true,
  });

  const parseContractNumbers = (value) => {
    if (Array.isArray(value)) {
      return [...new Set(value.map((x) => String(x).trim()).filter(Boolean))];
    }

    return [
      ...new Set(
        String(value || '')
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean),
      ),
    ];
  };

  // Fetch all accounts applies a filter based on the home-page link that was clicked (hccompany / account / contract)
  const getAccountsAll = () => {
    setAccountsData(undefined);
    const queryParams = {
      PageSize: 5000,
      field: 'accountId,accountName,hC_AccountID',
    };
    const csaActionData =
      persistDataStore.getState()?.userActionData?.csaUserActionInfo;
    if (csaActionData?.type && csaActionData?.value) {
      switch (csaActionData.type) {
        case 'hccompany':
          queryParams.Filter = `hC_AccountID:"${csaActionData.value}"`;
          break;
        case 'account':
          queryParams.Filter = `accountId:"${csaActionData.value}"`;
          break;
        case 'contract':
          queryParams.Filter = `contractNumber:"${csaActionData.value}"`;
          break;
        default:
          break;
      }
    }
    smartHttp
      .get(Endpoints.accountApi, queryParams)
      .then((res) => {
        setAccountsData(res.data?.results ?? []);
      })
      .catch(() => {
        setAccountsData([]);
      });
  };

  // Fetch contracts for a given accountId — used by Super Users
  const getContractsByAccount = (accountId, preSelectedContractNumbers = []) => {
    setContractsMultiSelect(undefined); // trigger loader
    const queryParams = {
      PageSize: 5000,
      Field: 'contractNumber,contractDescription,isVirtualContract,mappedContractNumber',
      Filter: `accountId:"${accountId}"`,
    };
    smartHttp
      .get(Endpoints.contractApi, queryParams)
      .then((res) => {
        const results = res.data?.results ?? [];
        const nonVCN = results.filter((c) => c.isVirtualContract !== 1);
        setContractsMultiSelect(
          nonVCN.map((c) => ({
            id: c.contractNumber,
            label: c.contractNumber,
            contractNumber: c.contractNumber,
            checked: preSelectedContractNumbers.includes(c.contractNumber),
          })),
        );
      })
      .catch(() => {
        setContractsMultiSelect([]);
      });
  };

  useEffect(() => {
    let isEditValue =
      locationDetails != null && locationDetails.officeLocationId != null
        ? true
        : false;
    setIsEdit(isEditValue);
    setInitialLocatonValue(getLocationValues());
    if (isEditValue && locationDetails.accountId) {
      // Edit: pre-select all mapped contracts from allContractNumber.
      const preSelectedContractNumbers = parseContractNumbers(
        locationDetails.allContractNumber || locationDetails.contractNumber,
      );
      setInitialContractNumbers(preSelectedContractNumbers);
      getContractsByAccount(
        locationDetails.accountId,
        preSelectedContractNumbers,
      );
    } else {
      // New: reset contract multi-select
      setContractsMultiSelect([]);
    }
    locationFormik.setFieldValue(locationDetails);
  }, [locationDetails]);

  useEffect(() => {
    try {
      if (isEdit) {
        if (
          JSON.stringify(initialLocatonValue) !==
          JSON.stringify(locationFormik.values)
        ) {
          setIsValueChanged(true);
        } else {
          setIsValueChanged(false);
        }
      }

      if (locationFormik.values.country) {
        setStateValues(locationFormik.values.country);
      }
    } catch (error) {}
  }, [locationFormik.values]);

  useEffect(() => {
    const selected = (contractsMultiSelect ?? []).filter((c) => c.checked);
    if (selected.length > 0) {
      setContractsMultiSelectError('');
    }

    if (!isEdit || !contractsMultiSelect?.length) return;
    const current = selected
      .map((c) => c.contractNumber)
      .sort()
      .join(',');
    const initial = [...initialContractNumbers].sort().join(',');
    if (current !== initial) {
      setIsValueChanged(true);
    } else {
      // Revert to form-level change detection when contracts match initial
      setIsValueChanged(
        JSON.stringify(initialLocatonValue) !==
          JSON.stringify(locationFormik.values),
      );
    }
  }, [contractsMultiSelect]);

  const handleFormSubmit = (e) => {
    const selected = (contractsMultiSelect ?? []).filter((c) => c.checked);
    if (selected.length === 0) {
      setContractsMultiSelectError('Please select at least one contract to proceed');
    } else {
      setContractsMultiSelectError('');
    }
    locationFormik.handleSubmit(e);
  };

  const onChangeHandler = (val, name) => {
    let value;

    if (name === 'contractNumber' && val && typeof val === 'object') {
      value = val.value;
    } else {
      value = val[name] ?? val;
    }

    let obj = {
      target: { name: name, value: value },
    };
    locationFormik.handleChange(obj);

    if (name === 'accountId') {
      setContractsMultiSelect([]);
      setContractsMultiSelectError('');
      if (value) {
        getContractsByAccount(value);
      }
    }

    if (name === 'country') {
      locationFormik.setFieldValue('state', '');
    }
  };

  const setStateValues = (countryName) => {
    let foundCountry = countryStateInfo?.CS_main?.find(
      (el) => el.country === countryName,
    );
    let stateList = foundCountry?.states?.map((el) => el?.state ?? el) ?? [];
    stateList = stateList.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );

    setCountryStateInfo({
      ...countryStateInfo,
      filteredState: stateList,
    });
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        backdrop="static"
      >
        {loading && <Loader type="scaleLoader" cssClass={loaderStyle} />}

        <Modal.Header closeButton>
          <span className="location-form-title">
            {isEdit ? 'Location Details' : 'Add a New Location'}
          </span>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleFormSubmit} className="row mb-0">
            <CommonDropdown
              mode="single"
              enableSearch={true}
              searchKeys={['accountName']}
              formLabel="Account"
              name="accountId"
              options={accountsData}
              formik={locationFormik}
              ddPlaceHolder={
                locationFormik.values.accountId !== '' &&
                accountsData?.length > 0
                  ? accountsData.find(
                      (el) =>
                        el.accountId === locationFormik.values.accountId,
                    )?.accountName ?? 'Select'
                  : 'Select'
              }
              labelKeyName="accountName"
              ddName="su-account"
              onChangeHandler={(e) => onChangeHandler(e, 'accountId')}
              addAsterisk
            />

            <Form.Group className="col-lg-6 col-md-12 mb-2 custom-dropdown">
              <Form.Label className="user-form-label">Contract</Form.Label>
              <span className="asterik">*</span>
              <CommonDropdown
                mode="multi"
                enableSearch={true}
                searchKeys={['label']}
                ddName="su-contracts-multi"
                labelKeyName="label"
                ddOptionsKey="id"
                options={contractsMultiSelect}
                setOptions={setContractsMultiSelect}
                enableSelectAll={true}
                dropDownDisabled={!locationFormik.values.accountId}
                ddPlaceHolder={(() => {
                  const selected = (contractsMultiSelect ?? []).filter(
                    (c) => c.checked,
                  );
                  if (selected.length === 0) return 'Select';
                  return `Contracts`;
                })()}
              />
              {contractsMultiSelectError && (
                <div className="form-input-error">
                  {contractsMultiSelectError}
                </div>
              )}
            </Form.Group>
            {/* <TextFieldInput
              formLabel="Location Name"
              placeholder="Location Name"
              id="officeLocationName"
              {...locationFormik.getFieldProps('officeLocationName')}
              formik={locationFormik}
              addAsterisk
              autoComplete="off"
            /> */}
            <TextFieldInput
              formLabel="Address 1"
              placeholder="Address 1"
              id="address_1"
              {...locationFormik.getFieldProps('address_1')}
              formik={locationFormik}
              autoComplete="off"
              addAsterisk
              autoFocus
            />
            <TextFieldInput
              formLabel="Address 2"
              placeholder="Address 2"
              id="address_2"
              {...locationFormik.getFieldProps('address_2')}
              formik={locationFormik}
              autoComplete="off"
            />
            <TextFieldInput
              formLabel="City"
              placeholder="City"
              id="city"
              disabled={isEdit}
              {...locationFormik.getFieldProps('city')}
              formik={locationFormik}
              autoComplete="off"
              addAsterisk
            />
            <TextFieldInput
              formLabel="Post Code"
              placeholder="Post Code"
              id="postalCode"
              disabled={isEdit}
              {...locationFormik.getFieldProps('postalCode')}
              formik={locationFormik}
              autoComplete="off"
              addAsterisk
            />
            <CommonDropdown
              mode="single"
              formLabel="Country"
              name="country"
              enableSearch={true}
              searchKeys={[]}
              disabled={isEdit}
              options={countryStateInfo?.filteredCountry}
              ddName="country"
              ddPlaceHolder={
                locationFormik.values.country !== ''
                  ? locationFormik.values.country
                  : 'Select'
              }
              {...locationFormik.getFieldProps('country')}
              onChangeHandler={(e) => onChangeHandler(e, 'country')}
              formik={locationFormik}
              addAsterisk
              autoComplete="off"
            />

            <CommonDropdown
              mode="single"
              formLabel="State"
              name="state"
              enableSearch={true}
              searchKeys={[]}
              disabled={isEdit}
              options={countryStateInfo?.filteredState}
              ddName="state"
              ddPlaceHolder={
                locationFormik.values.state !== ''
                  ? locationFormik.values.state
                  : 'Select'
              }
              {...locationFormik.getFieldProps('state')}
              onChangeHandler={(e) => onChangeHandler(e, 'state')}
              formik={locationFormik}
              customDropdownMessage="No states under the selected country"
              addAsterisk={(countryStateInfo?.filteredState ?? []).length > 0}
              autoComplete="off"
            />
            <Row className="pe-0">
              <Form.Group className="pe-0 mb-1 user-save-btn-body">
                <button
                  type="Submit"
                  className="btn btn-dark float-end btn-opacity"
                  disabled={isEdit && !isValueChanged}
                >
                  {isEdit ? 'Update Location' : 'Save Location'}
                </button>
              </Form.Group>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LocationAddEdit;
